import React, { useState, useMemo } from 'react';
import MaterialCard from './MaterialCard';
import './ResultsDisplay.css';

function ResultsDisplay({ results, onMaterialSelect }) {
    const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
    const [sortField, setSortField] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [filterText, setFilterText] = useState('');
    const [selectedType, setSelectedType] = useState('all');

    // Get unique material types from results
    const materialTypes = useMemo(() => {
        const types = new Set();
        results.forEach(material => {
            if (material.type_name) types.add(material.type_name);
        });
        return Array.from(types).sort();
    }, [results]);

    // Filter and sort results
    const processedResults = useMemo(() => {
        let filtered = [...results];

        // Apply text filter
        if (filterText) {
            const searchLower = filterText.toLowerCase();
            filtered = filtered.filter(material => 
                material.name?.toLowerCase().includes(searchLower) ||
                material.formula?.toLowerCase().includes(searchLower) ||
                material.description?.toLowerCase().includes(searchLower)
            );
        }

        // Apply type filter
        if (selectedType !== 'all') {
            filtered = filtered.filter(material => material.type_name === selectedType);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            // Handle null/undefined values
            if (aVal === null || aVal === undefined) aVal = '';
            if (bVal === null || bVal === undefined) bVal = '';

            // Convert to strings for comparison if needed
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [results, filterText, selectedType, sortField, sortDirection]);

    const handleSort = (field) => {
        if (sortField === field) {
            // Toggle direction if same field
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return '⇅';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const exportToCSV = () => {
        if (processedResults.length === 0) return;

        const headers = ['ID', 'Назва', 'Формула', 'Тип', 'Густина', 'Опис'];
        const rows = processedResults.map(material => [
            material.id,
            material.name || '',
            material.formula || '',
            material.type_name || '',
            material.density || '',
            material.description || ''
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => {
                const str = String(cell);
                if (str.includes(',') || str.includes('"')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            }).join(','))
        ].join('\n');

        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `materials_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (!results || results.length === 0) {
        return null;
    }

    return (
        <div className="results-display">
            {/* Controls Bar */}
            <div className="results-controls">
                <div className="controls-left">
                    <div className="results-count">
                        📊 Знайдено: <strong>{processedResults.length}</strong> 
                        {processedResults.length !== results.length && 
                            <span className="filtered-count"> (з {results.length})</span>
                        }
                    </div>

                    {/* Text Filter */}
                    <div className="filter-input-group">
                        <input
                            type="text"
                            placeholder="🔍 Фільтр по тексту..."
                            value={filterText}
                            onChange={(e) => setFilterText(e.target.value)}
                            className="text-filter-input"
                        />
                        {filterText && (
                            <button 
                                onClick={() => setFilterText('')}
                                className="clear-filter-btn"
                                title="Очистити"
                            >
                                ✕
                            </button>
                        )}
                    </div>

                    {/* Type Filter */}
                    {materialTypes.length > 0 && (
                        <select
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            className="type-filter-select"
                        >
                            <option value="all">Всі типи</option>
                            {materialTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    )}
                </div>

                <div className="controls-right">
                    {/* View Mode Toggle */}
                    <div className="view-toggle">
                        <button
                            className={`view-btn ${viewMode === 'cards' ? 'active' : ''}`}
                            onClick={() => setViewMode('cards')}
                            title="Картки"
                        >
                            🎴
                        </button>
                        <button
                            className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
                            onClick={() => setViewMode('table')}
                            title="Таблиця"
                        >
                            📋
                        </button>
                    </div>

                    {/* Export Button */}
                    <button 
                        onClick={exportToCSV}
                        className="export-csv-btn"
                        title="Експорт у CSV"
                    >
                        📥 CSV
                    </button>
                </div>
            </div>

            {/* Results Content */}
            {processedResults.length === 0 ? (
                <div className="no-filtered-results">
                    <div className="no-results-icon">🔍</div>
                    <p>Не знайдено результатів за вказаними фільтрами</p>
                    <button 
                        onClick={() => {
                            setFilterText('');
                            setSelectedType('all');
                        }}
                        className="reset-filters-btn"
                    >
                        Скинути фільтри
                    </button>
                </div>
            ) : viewMode === 'cards' ? (
                /* Card View */
                <div className="materials-grid">
                    {processedResults.map(material => (
                        <MaterialCard
                            key={material.id}
                            material={material}
                            onClick={() => onMaterialSelect(material.id)}
                        />
                    ))}
                </div>
            ) : (
                /* Table View */
                <div className="table-view-container">
                    <table className="results-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('id')} className="sortable">
                                    ID {getSortIcon('id')}
                                </th>
                                <th onClick={() => handleSort('name')} className="sortable">
                                    Назва {getSortIcon('name')}
                                </th>
                                <th onClick={() => handleSort('formula')} className="sortable">
                                    Формула {getSortIcon('formula')}
                                </th>
                                <th onClick={() => handleSort('type_name')} className="sortable">
                                    Тип {getSortIcon('type_name')}
                                </th>
                                <th onClick={() => handleSort('density')} className="sortable">
                                    Густина (кг/м³) {getSortIcon('density')}
                                </th>
                                <th>Опис</th>
                                <th>Дії</th>
                            </tr>
                        </thead>
                        <tbody>
                            {processedResults.map(material => (
                                <tr key={material.id} className="table-row">
                                    <td className="id-cell">{material.id}</td>
                                    <td className="name-cell">
                                        <strong>{material.name}</strong>
                                    </td>
                                    <td className="formula-cell">
                                        {material.formula || <span className="null-value">—</span>}
                                    </td>
                                    <td className="type-cell">
                                        <span className="type-badge">
                                            {material.type_name || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="density-cell">
                                        {material.density ? 
                                            material.density.toLocaleString() : 
                                            <span className="null-value">—</span>
                                        }
                                    </td>
                                    <td className="description-cell">
                                        {material.description ? 
                                            (material.description.length > 80 ? 
                                                material.description.substring(0, 80) + '...' : 
                                                material.description
                                            ) : 
                                            <span className="null-value">—</span>
                                        }
                                    </td>
                                    <td className="actions-cell">
                                        <button
                                            onClick={() => onMaterialSelect(material.id)}
                                            className="view-details-btn"
                                            title="Детальніше"
                                        >
                                            👁️
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Sort Info */}
            {processedResults.length > 0 && (
                <div className="sort-info">
                    Сортування: <strong>{
                        sortField === 'name' ? 'Назва' :
                        sortField === 'formula' ? 'Формула' :
                        sortField === 'type_name' ? 'Тип' :
                        sortField === 'density' ? 'Густина' :
                        'ID'
                    }</strong> ({sortDirection === 'asc' ? 'за зростанням' : 'за спаданням'})
                </div>
            )}
        </div>
    );
}

export default ResultsDisplay;
